type RuntimeEnv = {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_SUPABASE_PUBLISHABLE_KEY?: string;
};

const SUPABASE_HOST_SUFFIX = ".supabase.co";
const PROJECT_REF_PATTERN = /^[a-z0-9]{20}$/;

function normalizeEnvValue(value?: string): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function base64UrlDecode(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);

  if (typeof atob !== "function") {
    throw new Error("atob is not available");
  }

  return atob(padded);
}

function isSupabasePublishableKeyFormat(key?: string): boolean {
  return Boolean(key?.startsWith("sb_publishable_"));
}

export function resolveSupabaseKey(
  anonKey?: string,
  publishableKey?: string,
): string | undefined {
  const normalizedAnon = normalizeEnvValue(anonKey);
  const normalizedPublishable = normalizeEnvValue(publishableKey);
  const candidates = [normalizedAnon, normalizedPublishable].filter(
    (candidate): candidate is string => Boolean(candidate),
  );

  for (const candidate of candidates) {
    if (decodeSupabaseProjectRefFromJwt(candidate)) {
      return candidate;
    }
  }

  if (isSupabasePublishableKeyFormat(normalizedPublishable)) {
    return normalizedPublishable;
  }

  return normalizedAnon ?? normalizedPublishable;
}

function extractJwtPayload(key: string): { ref?: unknown } | undefined {
  const segments = key.split(".");
  if (segments.length < 2) return undefined;

  try {
    return JSON.parse(base64UrlDecode(segments[1])) as { ref?: unknown };
  } catch {
    return undefined;
  }
}

export function decodeSupabaseProjectRefFromJwt(key?: string): string | undefined {
  const normalized = normalizeEnvValue(key);
  if (!normalized) return undefined;

  const payload = extractJwtPayload(normalized);
  const rawRef = typeof payload?.ref === "string" ? payload.ref : undefined;
  if (!rawRef) return undefined;

  const projectRef = rawRef.trim().toLowerCase();
  return PROJECT_REF_PATTERN.test(projectRef) ? projectRef : undefined;
}

export function decodeSupabaseProjectRefFromUrl(rawUrl?: string): string | undefined {
  const normalizedUrl = normalizeEnvValue(rawUrl);
  if (!normalizedUrl) return undefined;

  try {
    const parsed = new URL(normalizedUrl);
    const hostname = parsed.hostname.toLowerCase();
    if (!hostname.endsWith(SUPABASE_HOST_SUFFIX)) return undefined;

    const projectRef = hostname.slice(0, -SUPABASE_HOST_SUFFIX.length);
    return PROJECT_REF_PATTERN.test(projectRef) ? projectRef : undefined;
  } catch {
    return undefined;
  }
}

export function buildSupabaseAuthStorageKey(rawUrl?: string): string | undefined {
  const projectRef = decodeSupabaseProjectRefFromUrl(rawUrl);
  return projectRef ? `sb-${projectRef}-auth-token` : undefined;
}

function isSupabaseUrl(url: URL): boolean {
  return url.protocol === "https:" && url.hostname.endsWith(SUPABASE_HOST_SUFFIX);
}

export function resolveSupabaseUrl(rawUrl: string | undefined, key?: string): string | undefined {
  const normalizedUrl = normalizeEnvValue(rawUrl);

  if (normalizedUrl) {
    try {
      const parsed = new URL(normalizedUrl);
      if (isSupabaseUrl(parsed)) {
        return parsed.origin;
      }
    } catch {
      // Continue to JWT-derived fallback.
    }
  }

  const projectRef = decodeSupabaseProjectRefFromJwt(key);
  if (!projectRef) {
    return undefined;
  }

  return `https://${projectRef}${SUPABASE_HOST_SUFFIX}`;
}

export function getSupabaseRuntimeConfig(env?: RuntimeEnv) {
  const activeEnv = env ?? (import.meta.env as unknown as RuntimeEnv);
  const anonKey = normalizeEnvValue(activeEnv.VITE_SUPABASE_ANON_KEY);
  const publishableKey = normalizeEnvValue(activeEnv.VITE_SUPABASE_PUBLISHABLE_KEY);
  const configuredUrlProjectRef = decodeSupabaseProjectRefFromUrl(activeEnv.VITE_SUPABASE_URL);
  const preferredAnonKey =
    configuredUrlProjectRef && isSupabasePublishableKeyFormat(anonKey) ? anonKey : undefined;
  const key = preferredAnonKey ?? resolveSupabaseKey(anonKey, publishableKey);
  const url = resolveSupabaseUrl(activeEnv.VITE_SUPABASE_URL, key);
  const urlProjectRef = decodeSupabaseProjectRefFromUrl(url);
  const keyProjectRef = decodeSupabaseProjectRefFromJwt(key);

  if (urlProjectRef && keyProjectRef && urlProjectRef !== keyProjectRef) {
    throw new Error(
      `Supabase project ref mismatch: URL has ${urlProjectRef}, key has ${keyProjectRef}`,
    );
  }

  return { url, key, anonKey, publishableKey };
}
